import React, { useState } from 'react'
import {
  CreditCard as CreditCardIcon,
  AssuredWorkload as AssuredWorkloadIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Business as BusinessIcon,
  Biotech as BiotechIcon,
  Payments as PaymentsIcon,
  Inventory2 as Inventory2Icon,
  MenuBook as MenuBookIcon,
  ManageAccounts as ManageAccountsIcon,
  ScienceOutlined as ScienceOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Science as ScienceIcon,
  Keyboard,
  KeyboardAlt,
} from '@mui/icons-material'
import BankEstablishmentsTable from '@components/tables/BankEstablishmentsTable'
import BankAccountsTable from '@components/tables/BankAccountsTable'
import BillingCompaniesTable from '@components/tables/BillingCompaniesTable'
import IsotopesTable from '@components/tables/IsotopesTable'
import PaymentModesTable from '@components/tables/PaymentModesTable'
import { Tabs, Tab } from '@mui/material'
import { TabContext, TabPanel } from '@mui/lab'
import logo from '../../../../src/assets/img/logos/ariane-fse.png'
import CompoundsTable from '@components/tables/CompoundsTable'
import SuppliersTable from '@components/tables/SuppliersTable'
import StorageLocationsTable from '@components/tables/StorageLocationTable'
import ProductFamiliesTable from '@components/tables/ProductFamiliesTable'
import ProductCatalogsTable from '@components/tables/ProductCatalogsTable'
import InjectionSitesTable from '@components/tables/InjectionSiteTable'
import WasteContainersTable from '@components/tables/WasteContainersTable'
import HotLabEquipmentsTable from '@components/tables/HotLabEquipmentsTable'
import VisualQualityControlsTable from '@components/tables/VisualQualityControlsTable'
import CustomQualityControlsTable from '@components/tables/CustomQualityControlsTable'
import QualityControlAssignmentsTable from '@components/tables/QualityControlAssignmentsTable'
import ActivityLimitsTable from '@components/tables/ActivityLimitsTable'
import CompoundProductionStepsTable from '@components/tables/CompoundProductionStepsTable'
import CompoundProductionStepAssignmentsTable from '@components/tables/CompoundProductionStepAssignmentsTable'
import PreparationDocumentsTable from '@components/tables/PreparationDocumentsTable'
import ActivimeterDetailsTable from '@components/tables/ActivimeterDetailsTable'
import ActivimeterCalibrationsTable from '@components/tables/ActivimeterCalibrationsTable'
import ProductOrdersTable from '@components/tables/ProductOrdersTable'
import StocksTable from '@components/tables/StocksTable'
import { useLocation } from 'react-router-dom'
import WorkbenchesTable from '@components/tables/WorkbenchesTable'
import { HotKeys } from './Hotkeys'
import { Layers, SettingsIcon, UserCog2 } from 'lucide-react'
import MemberPreferenceConfig from './MemberPreferenceConfig'
import AcquisitionProtocols from '../acquisition_protocols/AcqusitionProtocols'
import { useIntl } from 'react-intl'
import { VoiceCommands } from './VoiceCommands'

type SubTab = {
  label: string
  content: JSX.Element
}

type Tab = {
  label: string
  icon: JSX.Element
  content?: JSX.Element
  subTabs?: SubTab[]
  index?: number
}

function SubTabs({ tabs, index: _index }: { tabs: SubTab[]; index?: number }) {
  const [index, setIndex] = useState(_index ?? 0)

  function a11yProps(index: number) {
    return {
      id: `sub-tab-${index}`,
      'aria-controls': `sub-tabpanel-${index}`,
    }
  }

  return (
    <TabContext value={String(index)}>
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        value={index}
        onChange={(_, newValue) => setIndex(newValue)}
        aria-label="sub-tabs"
        sx={{
          backgroundColor: 'white',
          minHeight: '40px',
          '& .MuiTab-root': {
            minHeight: '40px',
            padding: '2px 12px',
            fontSize: '0.875rem',
          },
          '& .MuiTab-iconWrapper': {
            marginRight: '8px',
          },
        }}
      >
        {tabs.map((sub, idx) => (
          <Tab key={`sub-${idx}`} label={sub.label} {...a11yProps(idx)} />
        ))}
      </Tabs>

      {tabs.map((sub, idx) => (
        <TabPanel
          key={`sub-panel-${idx}`}
          value={String(idx)}
          sx={{ p: 0, pt: 2 }}
        >
          {sub.content}
        </TabPanel>
      ))}
    </TabContext>
  )
}

export default function Settings() {
  const location = useLocation()
  const redirectToOrders = location.pathname.split('/').pop() === 'orders'
  const [parentIndex, setParentIndex] = useState(redirectToOrders ? 1 : 0)
  const [childIndex, setChildIndex] = useState(redirectToOrders ? 3 : 0)
  const intl = useIntl()

  const controlTabs: SubTab[] = [
    { label: 'Visuels', content: <VisualQualityControlsTable /> },
    { label: 'Personnalisés', content: <CustomQualityControlsTable /> },
    { label: 'Affectations', content: <QualityControlAssignmentsTable /> },
  ]
  const preparationTabs: SubTab[] = [
    { label: 'Limites activité', content: <ActivityLimitsTable /> },
    { label: 'Étapes production', content: <CompoundProductionStepsTable /> },
    {
      label: 'Affectations',
      content: <CompoundProductionStepAssignmentsTable />,
    },
    { label: 'Documents', content: <PreparationDocumentsTable /> },
    { label: 'Paramétrage activimètres', content: <ActivimeterDetailsTable /> },
    {
      label: 'Calibrage activimètres',
      content: <ActivimeterCalibrationsTable />,
    },
  ]
  const productsTabs: SubTab[] = [
    { label: 'Fournisseurs', content: <SuppliersTable /> },
    { label: 'Familles', content: <ProductFamiliesTable /> },
    { label: 'Catalogues', content: <ProductCatalogsTable /> },
    { label: 'Commandes', content: <ProductOrdersTable /> },
    { label: 'Stocks', content: <StocksTable /> },
  ]
  const infrastructureTabs: SubTab[] = [
    { label: 'Stockages', content: <StorageLocationsTable /> },
    { label: 'Conteneurs de déchets', content: <WasteContainersTable /> },
    { label: 'Matériels de laboratoire', content: <HotLabEquipmentsTable /> },
    { label: 'Enceintes blindées', content: <WorkbenchesTable /> },
  ]
  const productionTabs: SubTab[] = [
    { label: 'Isotopes', content: <IsotopesTable /> },
    { label: 'MRP', content: <CompoundsTable /> },
  ]
  const exploitationTabs: SubTab[] = [
    { label: 'Sites d\'injection', content: <InjectionSitesTable /> },
  ]

  const childrens: Tab[][] = [
    [
      {
        label: 'Modes de paiement',
        icon: <CreditCardIcon />,
        content: <PaymentModesTable />,
      },
      {
        label: 'Etablissements bancaires',
        icon: <AssuredWorkloadIcon />,
        content: <BankEstablishmentsTable />,
      },
      {
        label: 'Comptes bancaires',
        icon: <AccountBalanceWalletIcon />,
        content: <BankAccountsTable />,
      },
      {
        label: 'Sociétés de facturation',
        icon: <BusinessIcon />,
        content: <BillingCompaniesTable />,
      },
    ],
    [
      {
        label: 'Production',
        icon: <ScienceOutlinedIcon />,
        subTabs: productionTabs,
      },
      {
        label: 'Exploitation',
        icon: <ManageAccountsIcon />,
        subTabs: exploitationTabs,
      },
      {
        label: 'Infrastructures',
        icon: <Inventory2Icon />,
        subTabs: infrastructureTabs,
      },
      { label: 'Produits', icon: <MenuBookIcon />, subTabs: productsTabs },
      {
        label: 'Contrôle',
        icon: <CheckCircleOutlineIcon />,
        subTabs: controlTabs,
      },
      { label: 'Préparation', icon: <ScienceIcon />, subTabs: preparationTabs },
    ],
    [
      {
        label: intl.formatMessage({ id: 'hotkeys' }),
        icon: <KeyboardAlt />,
        content: <HotKeys />,
      },
      {
        label: 'Voice Commands',
        icon: <KeyboardAlt />,
        content: <VoiceCommands />,
      },
    ],
    [
      {
        label: intl.formatMessage({ id: 'acquisition-protocols' }),
        icon: <Layers />,
        content: <AcquisitionProtocols />,
      },
      {
        label: intl.formatMessage({ id: 'member-preference-config' }),
        icon: <UserCog2 />,
        content: <MemberPreferenceConfig />,
      },
    ],
  ]

  const currentChildren = childrens[parentIndex]

  function a11yProps(index: number) {
    return {
      id: `child-tab-${index}`,
      'aria-controls': `child-tabpanel-${index}`,
    }
  }

  const handleChildChange = (_: React.SyntheticEvent, newValue: number) =>
    setChildIndex(newValue)
  const handleParentChange = (_: React.SyntheticEvent, newValue: number) => {
    setParentIndex(newValue)
    setChildIndex(0)
  }

  return (
    <>
      <div>
        <Tabs
          value={parentIndex}
          onChange={handleParentChange}
          aria-label="parent tabs"
          sx={{
            backgroundColor: 'white',
            minHeight: '48px',
            '& .MuiTab-root': {
              minHeight: '48px',
              padding: '6px 12px',
              fontSize: '1rem',
            },
            '& .MuiTab-iconWrapper': { marginRight: '8px' },
          }}
        >
          <Tab
            icon={<PaymentsIcon />}
            iconPosition="start"
            label="Facturation"
            {...a11yProps(0)}
          />
          <Tab
            icon={<BiotechIcon />}
            iconPosition="start"
            label="Radiopharmacie"
            {...a11yProps(1)}
          />
          <Tab
            icon={<Keyboard />}
            iconPosition="start"
            label={intl.formatMessage({ id: 'hotkeys' })}
            {...a11yProps(2)}
          />
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label={intl.formatMessage({ id: 'report-settings' })}
            {...a11yProps(3)}
          />
        </Tabs>
      </div>

      <TabContext value={String(childIndex)}>
        <div>
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            value={childIndex}
            onChange={handleChildChange}
            aria-label="settings child tabs"
            sx={{
              backgroundColor: 'white',
              minHeight: '40px',
              '& .MuiTab-root': {
                minHeight: '40px',
                padding: '4 12px',
                fontSize: '0.78rem',
              },
              '& .MuiTab-iconWrapper': { marginRight: '6px' },
            }}
          >
            {currentChildren.map((t, idx) => (
              <Tab
                key={`child-${parentIndex}-${idx}`}
                iconPosition="start"
                icon={t.icon}
                label={t.label}
                {...a11yProps(idx)}
              />
            ))}
          </Tabs>

          {currentChildren.map((t, idx) => (
            <TabPanel
              key={`panel-${parentIndex}-${idx}`}
              value={String(idx)}
              sx={t.subTabs ? { p: 0 } : undefined}
            >
              {t.subTabs ? (
                <SubTabs
                  tabs={t.subTabs}
                  index={redirectToOrders ? 3 : undefined}
                />
              ) : (
                t.content
              )}
            </TabPanel>
          ))}
        </div>
      </TabContext>

      <div className="absolute rounded-full top-6 right-6 -translate-y-1/2">
        <img src={logo} alt="logo" style={{ width: '176px' }} />
      </div>
    </>
  )
}
